import React, { useMemo, useState } from "react";
import { Globe2, ArrowRight, Bookmark, Search } from "lucide-react";
import { api, apiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { toast } from "sonner";
import { useAuth } from "../../context/AuthContext";
import { hasPerm } from "../../lib/permissions";
import { PageHead, Field, Empty, usePorts, CONTAINERS, CONGESTION_STYLE } from "./common";

export default function RouteExplorer() {
  const { user } = useAuth();
  const ports = usePorts();
  const [originCountry, setOriginCountry] = useState("");
  const [destCountry, setDestCountry] = useState("");
  const [q, setQ] = useState("");
  const [container, setContainer] = useState("20FT");
  const canCreate = hasPerm(user, "sea_freight", "create");

  const countries = useMemo(() => [...new Set(ports.map((p) => p.country))].sort(), [ports]);
  const origins = useMemo(() => ports.filter((p) => (!originCountry || p.country === originCountry)
    && (!q || `${p.port_name} ${p.unlocode}`.toLowerCase().includes(q.toLowerCase()))).slice(0, 40), [ports, originCountry, q]);
  const destinations = useMemo(() => ports.filter((p) => !destCountry || p.country === destCountry).slice(0, 40), [ports, destCountry]);
  const [origin, setOrigin] = useState(null);
  const [dest, setDest] = useState(null);

  const saveRoute = async () => {
    if (!origin || !dest) { toast.error("Pick an origin and a destination port"); return; }
    try {
      await api.post("/sea-freight/saved-routes", {
        origin_port_id: origin.id, destination_port_id: dest.id, container_type: container,
      });
      toast.success(`Saved ${origin.port_name} → ${dest.port_name}`);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const PortList = ({ rows, selected, onSelect, testid }) => (
    <div className="bg-white border border-slate-200 rounded-sm max-h-[420px] overflow-y-auto divide-y divide-slate-100" data-testid={testid}>
      {rows.length === 0 && <div className="px-4 py-8 text-sm text-slate-400 text-center">No port matched</div>}
      {rows.map((p) => (
        <button key={p.id} type="button" data-testid={`${testid}-${p.unlocode}`}
          onClick={() => onSelect(p)}
          className={`w-full text-left px-4 py-2.5 hover:bg-slate-50 ${selected?.id === p.id ? "bg-slate-100" : ""}`}>
          <div className="flex items-center justify-between gap-2">
            <div>
              <div className="text-sm text-slate-800">{p.port_name} <span className="text-slate-400 font-mono text-xs">{p.unlocode}</span></div>
              <div className="text-[11px] text-slate-500">{p.country} · {p.region}</div>
            </div>
            <Badge variant="outline" className={`rounded-full px-2 text-[10px] ${CONGESTION_STYLE[p.congestion_level]}`}>{p.congestion_level}</Badge>
          </div>
        </button>
      ))}
    </div>
  );

  return (
    <div data-testid="route-explorer-page">
      <PageHead title="Global Route Explorer"
        subtitle="Browse worldwide ports by country and region, pick a lane and save it for monitoring."
        right={canCreate && <Button data-testid="explorer-save-route" className="bg-[#0B132B] hover:bg-[#1C2541] text-white rounded-sm gap-1.5" onClick={saveRoute}>
          <Bookmark className="w-4 h-4" /> Save this route
        </Button>} />

      <div className="bg-white border border-slate-200 rounded-sm p-4 mb-4 grid grid-cols-1 md:grid-cols-4 gap-3">
        <Field label="Search port">
          <div className="relative">
            <Search className="w-4 h-4 text-slate-400 absolute left-2.5 top-2.5" />
            <Input data-testid="explorer-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Port name or UN/LOCODE" className="bg-white h-9 pl-8" />
          </div>
        </Field>
        <Field label="Origin country">
          <Select value={originCountry || "all"} onValueChange={(v) => setOriginCountry(v === "all" ? "" : v)}>
            <SelectTrigger data-testid="explorer-origin-country" className="bg-white h-9"><SelectValue /></SelectTrigger>
            <SelectContent className="max-h-72"><SelectItem value="all">All countries</SelectItem>{countries.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
          </Select>
        </Field>
        <Field label="Destination country">
          <Select value={destCountry || "all"} onValueChange={(v) => setDestCountry(v === "all" ? "" : v)}>
            <SelectTrigger data-testid="explorer-dest-country" className="bg-white h-9"><SelectValue /></SelectTrigger>
            <SelectContent className="max-h-72"><SelectItem value="all">All countries</SelectItem>{countries.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
          </Select>
        </Field>
        <Field label="Container type">
          <Select value={container} onValueChange={setContainer}>
            <SelectTrigger data-testid="explorer-container" className="bg-white h-9"><SelectValue /></SelectTrigger>
            <SelectContent>{CONTAINERS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
          </Select>
        </Field>
      </div>

      <div className="bg-slate-50 border border-slate-200 rounded-sm px-4 py-3 mb-4 flex flex-wrap items-center gap-3 text-sm" data-testid="explorer-selection">
        <Globe2 className="w-4 h-4 text-slate-400" />
        <span className="text-slate-800 font-medium">{origin ? `${origin.port_name} (${origin.unlocode})` : "Select origin"}</span>
        <ArrowRight className="w-4 h-4 text-slate-400" />
        <span className="text-slate-800 font-medium">{dest ? `${dest.port_name} (${dest.unlocode})` : "Select destination"}</span>
        <span className="text-slate-400">· {container}</span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div>
          <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-500 mb-2">Origin ports</h2>
          <PortList rows={origins} selected={origin} onSelect={setOrigin} testid="explorer-origin-list" />
        </div>
        <div>
          <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-500 mb-2">Destination ports</h2>
          <PortList rows={destinations} selected={dest} onSelect={setDest} testid="explorer-dest-list" />
        </div>
      </div>

      {ports.length === 0 && <Empty testid="explorer-empty">Loading worldwide port database...</Empty>}
    </div>
  );
}
